
import { useState } from 'react';
import { useFirebase } from '@/context/FirebaseProvider'; // For currentUser
import { useUserOperations } from '@/context/schema/useUserOperations';
import { usePortfolioOperations } from '@/context/schema/usePortfolioOperations';
import { useBusinessOperations } from '@/context/schema/useBusinessOperations';
import { Portfolio as PortfolioZod } from '@/schemas/portfolio'; // Zod type for portfolio data
import { Business as BusinessZod, BusinessStatus, Member as MemberZod } from '@/schemas/business'; // Zod types
import { User as UserZod } from '@/schemas/user'; // Zod type for user data
import { MemberRole, MemberStatus } from '@/types/schema'; 

import { v4 as uuidv4 } from 'uuid';
import { formatDate } from '@/utils/dateUtils';
import { getAuth, fetchSignInMethodsForEmail, createUserWithEmailAndPassword, User as FirebaseUserType } from 'firebase/auth';
// serverTimestamp is already imported by operation hooks, but direct use might be needed if not calling them for everything.
// For now, assuming operation hooks handle their own timestamps.
// import { doc, getDoc, setDoc, updateDoc, arrayUnion, collection, serverTimestamp } from 'firebase/firestore';
// import { db } from '@/utils/firebase';
// import { PORTFOLIOS_COLLECTION, BUSINESS_COLLECTION, USERS_COLLECTION } from '@/utils/firestore/constants';
// import { addBusinessToUser } from '@/utils/firestore/users'; // This should be handled by useUserOperations or useBusinessOperations
import { defaultBusinessHours } from '@/components/business/onboarding/types/leadDeliveryTypes';

interface BusinessDetails {
  name: string;
  shortName?: string;
  phone?: string;
  email?: string;
  website?: string;
  foundingDate?: string;
  founded?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  industry: string;
  industryId?: string; // Added industryId
  industryVertical?: string;
  timeZone?: string;
  coordinates?: { latitude: number; longitude: number }; // Corrected to match context's BusinessDetails
}

export const useFormSubmit = (
  businessDetails: BusinessDetails,
  userEmail: string,
  onComplete: (businessId: string) => void,
  marketingOptIn: boolean = false
) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null); // Added for form-specific errors
  const auth = getAuth();
  const { userData: currentUserFromProvider } = useFirebase(); // Get userData (which includes id)
  
  // Pass currentUserFromProvider (which is UserZod | null | undefined) to the operation hooks
  const userOps = useUserOperations(currentUserFromProvider); 
  const portfolioOps = usePortfolioOperations(currentUserFromProvider);
  const businessOps = useBusinessOperations(currentUserFromProvider);


  const handleSubmit = async () => {
    console.log("handleSubmit: Starting form submission process.");
    setIsSubmitting(true);
    setFormError(null); // Reset error on new submission
    let finalUserUid: string | null = null;
    let currentAuthUser: FirebaseUserType | null = auth.currentUser;

    try {
      // Step A: User Authentication & Creation
      console.log("handleSubmit: Step A - User Authentication & Creation.");
      if (!currentAuthUser) {
        console.log("handleSubmit: No current authenticated user found. Attempting to create new user.");
        const signInMethods = await fetchSignInMethodsForEmail(auth, userEmail);
        if (signInMethods && signInMethods.length > 0) {
          console.log("handleSubmit: Email already registered.");
          setFormError("This email is already registered. Please use a different email or log in.");
          setIsSubmitting(false);
          return;
        }
        const randomPassword = uuidv4();
        console.log("handleSubmit: Creating user with email and random password.");
        const userCredential = await createUserWithEmailAndPassword(auth, userEmail, randomPassword);
        currentAuthUser = userCredential.user;
        finalUserUid = currentAuthUser.uid;

        const newUserData: Omit<UserZod, 'id' | 'createdAt' | 'updatedAt'> = { // Use Omit for input type
          email: currentAuthUser.email,
          displayName: currentAuthUser.email,
          firstName: '',
          lastName: '',
          portfolios: [],
          businesses: [],
          // role, customClaims, permissions, etc., can have defaults if needed by userSchema
        };
        console.log(`handleSubmit: Creating Firestore user document for UID: ${finalUserUid}`);
        await userOps.createUser(finalUserUid, newUserData);
        console.log(`handleSubmit: New Firebase Auth user and Firestore user document created for UID: ${finalUserUid}`);
      } else {
        finalUserUid = currentAuthUser.uid;
        console.log(`handleSubmit: Using existing logged-in user: ${finalUserUid}`);
      }

      if (!finalUserUid) {
        console.error("handleSubmit: User UID is not available after auth check/creation.");
        throw new Error("User UID is not available after auth check/creation.");
      }
      console.log("handleSubmit: Step A complete. User UID:", finalUserUid);

      // Step B: Portfolio Creation
      console.log("handleSubmit: Step B - Portfolio Creation.");
      const portfolioDisplayName = 'My Portfolio';
      const newPortfolioData: Omit<PortfolioZod, 'id' | 'createdAt' | 'updatedAt' | 'members' | 'businessRefs'> = {
        name: portfolioDisplayName,
        primaryOwnerRef: finalUserUid,
        // members array will be initialized by createPortfolio operation hook
      };
      console.log(`handleSubmit: Creating new portfolio for user ${finalUserUid}.`);
      // createPortfolio in usePortfolioOperations now handles adding owner to members and linking to user
      const createdPortfolioId = await portfolioOps.createPortfolio(newPortfolioData, finalUserUid);
      console.log(`handleSubmit: New portfolio created with ID: ${createdPortfolioId} and linked to user ${finalUserUid}`);
      console.log("handleSubmit: Step B complete. Portfolio ID:", createdPortfolioId);


      // Step C: Business Creation
      console.log("handleSubmit: Step C - Business Creation.");
      const dateString = businessDetails.foundingDate || businessDetails.founded || '';
      const formattedDate = dateString ? formatDate(new Date(dateString)) : '';

      const coreBusinessData: Omit<BusinessZod, 'id' | 'createdAt' | 'updatedAt' | 'members' | 'portfolioId'> = {
        name: businessDetails.name,
        displayName: businessDetails.shortName,
        shortName: businessDetails.shortName,
        phone: businessDetails.phone,
        email: businessDetails.email || userEmail,
        website: businessDetails.website,
        founded: formattedDate,
        address: businessDetails.address,
        city: businessDetails.city,
        state: businessDetails.state,
        zip: businessDetails.zip,
        industry: businessDetails.industry, // This now holds the name
        industryId: businessDetails.industryId, // Save the ID
        industryVertical: businessDetails.industryVertical || '',
        timeZone: businessDetails.timeZone || '',
        coordinates: businessDetails.coordinates && 
          typeof businessDetails.coordinates.latitude === 'number' && 
          typeof businessDetails.coordinates.longitude === 'number' 
          ? { latitude: businessDetails.coordinates.latitude, longitude: businessDetails.coordinates.longitude } 
          : null,
        // Default other fields as necessary per businessSchema
        status: 'onboarding' as BusinessStatus,
        marketingOptIn: marketingOptIn,
        // servicesData, promotionData, etc., can be defaulted if needed or handled by subsequent steps
        servicesData: { services: [], quickFilters: {}, hasBeenSaved: false },
        promotionData: { promotions: [], showAll: false, hasBeenSaved: false },
        differentiatorData: { selectedDifferentiators: [], yearsExperience: null, hasBeenSaved: false },
        leadDeliveryData: { // Default structure based on baseLeadDeliveryDataSchema
          customBusinessHours: false,
          emailDelivery: false,
          phoneDelivery: false,
          emailUsers: [],
          phoneUsers: [],
          emails: [],
          phones: [],
          hasBeenSaved: false,
          businessHours: {
            // Convert the array to an object with lowercase day names as keys
            monday: { open: defaultBusinessHours[0].active, openTime: defaultBusinessHours[0].startTime, closeTime: defaultBusinessHours[0].endTime },
            tuesday: { open: defaultBusinessHours[1].active, openTime: defaultBusinessHours[1].startTime, closeTime: defaultBusinessHours[1].endTime },
            wednesday: { open: defaultBusinessHours[2].active, openTime: defaultBusinessHours[2].startTime, closeTime: defaultBusinessHours[2].endTime },
            thursday: { open: defaultBusinessHours[3].active, openTime: defaultBusinessHours[3].startTime, closeTime: defaultBusinessHours[3].endTime },
            friday: { open: defaultBusinessHours[4].active, openTime: defaultBusinessHours[4].startTime, closeTime: defaultBusinessHours[4].endTime },
            saturday: { open: defaultBusinessHours[5].active, openTime: defaultBusinessHours[5].startTime, closeTime: defaultBusinessHours[5].endTime },
            sunday: { open: defaultBusinessHours[6].active, openTime: defaultBusinessHours[6].startTime, closeTime: defaultBusinessHours[6].endTime },
          },
        },
        locationData: {
          targetAllUS: true,
          radiusMiles: 50,
          businessCoordinates: businessDetails.coordinates && 
            typeof businessDetails.coordinates.latitude === 'number' && 
            typeof businessDetails.coordinates.longitude === 'number' 
            ? { latitude: businessDetails.coordinates.latitude, longitude: businessDetails.coordinates.longitude } 
            : null,
          locationsByRadius: [],
          targetedStates: [],
          targetedCounties: {},
          blockedZipCodes: [],
          hasBeenSaved: false,
        },
      };
      
      console.log(`handleSubmit: Creating new business linked to portfolio ${createdPortfolioId} and user ${finalUserUid}.`);
      // createBusiness in useBusinessOperations handles adding owner to members and linking to user/portfolio
      const createdBusinessId = await businessOps.createBusiness(coreBusinessData, createdPortfolioId, finalUserUid);
      console.log(`handleSubmit: Business document created with ID: ${createdBusinessId}, linked to portfolio ${createdPortfolioId} and user ${finalUserUid}`);
      
      console.log("handleSubmit: Business creation process complete. Calling onComplete.");
      onComplete(createdBusinessId);

    } catch (error) {
      console.error('handleSubmit: Error during submission process:', error);
      // Handle duplicate email errors from Firebase Auth
      const err = error as { code?: string } | undefined;
      if (err && err.code === 'auth/email-already-in-use') {
        setFormError('This email is already registered. Please use a different email or log in.');
      } else {
        setFormError('An unexpected error occurred. Please try again.');
      }
    } finally {
      console.log("handleSubmit: Submission process finished.");
      setIsSubmitting(false);
    }
  };

  return {
    handleSubmit,
    isSubmitting,
    formError // Expose formError
  };
};
